import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { PlayerRole, getLanguage } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

export const UndercoverRoleAssignment: React.FC = () => {
  const { gameState, myRole, currentRoom } = useSocket();
  const t = useTranslations();
  const [lang, setLang] = React.useState<'en' | 'zh'>(getLanguage());
  
  // Listen for language changes
  React.useEffect(() => {
    const handleLanguageChange = () => {
      setLang(getLanguage());
    };
    window.addEventListener('languagechange', handleLanguageChange);
    return () => window.removeEventListener('languagechange', handleLanguageChange);
  }, []);
  
  if (!gameState || !myRole || !currentRoom) {
    return (
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 px-2">
          {!gameState ? t.games.undercover.loadingGame : t.games.undercover.waitingForWordAssignment}
        </h2>
        <p className="text-sm sm:text-base text-gray-600 px-2">
          {!gameState ? t.games.undercover.pleaseWait : t.games.undercover.assigningWords}
        </p>
      </div>
    );
  }

  const isWordsmith = myRole === PlayerRole.WORDSMITH;
  const isBlank = myRole === PlayerRole.BLANK;
  
  // Get word based on role and current language
  let word = '';
  if (myRole === PlayerRole.CIVILIAN) {
    word = lang === 'zh' 
      ? (gameState.civilianWordZh || gameState.civilianWord || '')
      : (gameState.civilianWord || gameState.civilianWordZh || '');
  } else if (myRole === PlayerRole.UNDERCOVER) {
    word = lang === 'zh'
      ? (gameState.undercoverWordZh || gameState.undercoverWord || '')
      : (gameState.undercoverWord || gameState.undercoverWordZh || '');
  }


  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-2 px-2">
          {t.games.undercover.wordAssignment}
        </h2>
      </div>

      {/* Show word for non-wordsmith players */}
      {!isWordsmith && (
        <div className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-indigo-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3 flex items-center gap-2">
            <span className="text-lg sm:text-xl">📖</span>
            {t.games.undercover.yourWord}
          </h3>
          {word ? (
            <div className="w-full overflow-x-auto">
              <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-gray-800 text-center mb-2 sm:mb-3 whitespace-nowrap">
                {word}
              </p>
            </div>
          ) : isBlank ? (
            <div>
              <p className="text-lg sm:text-xl font-semibold text-gray-600 text-center mb-2 sm:mb-3">
                {t.games.undercover.blank}
              </p>
              <div className="mt-4 p-3 sm:p-4 rounded-lg bg-yellow-100 text-yellow-900">
                <p className="text-sm sm:text-base font-semibold">
                  {t.games.undercover.blankHint}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-base sm:text-lg text-gray-500 text-center mb-2 sm:mb-3">
              {t.games.undercover.loading}
            </p>
          )}
        </div>
      )}

      {/* Wordsmith view - show submitted words */}
      {isWordsmith && (
        <div className="w-full bg-orange-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6 border-2 border-orange-300">
          <h3 className="text-base sm:text-lg font-semibold text-orange-800 mb-2 sm:mb-3 flex items-center gap-2">
            <span className="text-lg sm:text-xl">👀</span>
            {t.games.undercover.observerMode}
          </h3>
          <p className="text-base sm:text-lg text-orange-900 mb-4">
            {t.games.undercover.wordsmithObserverMessage}
          </p>
          {/* Show submitted words */}
          {(gameState.wordsmithCivilianWord || gameState.wordsmithUndercoverWord) && (
            <div className="space-y-3 mt-4 pt-4 border-t-2 border-orange-200">
              <div className="p-3 bg-white rounded-lg border border-orange-200">
                <p className="text-xs sm:text-sm text-orange-700 mb-1 font-semibold">
                  {t.games.undercover.civilianWord}:
                </p>
                <div className="w-full overflow-x-auto">
                  <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-orange-900 whitespace-nowrap">
                    {lang === 'zh' && gameState.wordsmithCivilianWordZh 
                      ? gameState.wordsmithCivilianWordZh 
                      : gameState.wordsmithCivilianWord}
                  </p>
                </div>
              </div>
              <div className="p-3 bg-white rounded-lg border border-orange-200">
                <p className="text-xs sm:text-sm text-orange-700 mb-1 font-semibold">
                  {t.games.undercover.undercoverWord}:
                </p>
                <div className="w-full overflow-x-auto">
                  <p className="text-[clamp(1.5rem,8vw,3rem)] font-bold text-orange-900 whitespace-nowrap">
                    {lang === 'zh' && gameState.wordsmithUndercoverWordZh 
                      ? gameState.wordsmithUndercoverWordZh 
                      : gameState.wordsmithUndercoverWord}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};
